import React, { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export function Lightbox({ images, index, onClose, onPrev, onNext }) {
  const img = images[index];
  const hasMultiple = images.length > 1;

  const onKey = useCallback((e) => {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowLeft"  && hasMultiple) onPrev();
    if (e.key === "ArrowRight" && hasMultiple) onNext();
  }, [onClose, onPrev, onNext, hasMultiple]);

  useEffect(() => {
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onKey]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/92 flex items-center justify-center"
      onClick={onClose}
      data-testid="lightbox-overlay"
    >
      {/* Close - fixed to viewport top-right, always visible */}
      <button
        onClick={onClose}
        style={{ position: "fixed", top: 16, right: 16, zIndex: 110 }}
        className="w-11 h-11 rounded-full bg-black/70 border border-white/20 text-white flex items-center justify-center hover:bg-white/20 transition-colors shadow-xl"
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <X size={22} />
      </button>

      {/* Prev - fixed to viewport left-center */}
      {hasMultiple && (
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          style={{ position: "fixed", left: 12, top: "50%", transform: "translateY(-50%)", zIndex: 110 }}
          className="w-11 h-11 rounded-full bg-black/70 border border-white/20 text-white flex items-center justify-center hover:bg-white/20 transition-colors shadow-xl"
          aria-label="Previous"
          data-testid="lightbox-prev"
        >
          <ChevronLeft size={24} />
        </button>
      )}

      {/* Image + caption */}
      <div
        className="flex flex-col items-center gap-3 px-16"
        style={{ maxWidth: "100vw", maxHeight: "100vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={img.src}
          alt={img.title}
          style={{ maxWidth: "min(90vw, 1200px)", maxHeight: "80vh", objectFit: "contain" }}
          className="rounded-lg shadow-2xl"
          data-testid="lightbox-image"
        />
        {(img.title || img.desc) && (
          <div className="text-center px-4">
            {img.title && <div className="text-white font-heading font-bold text-sm">{img.title}</div>}
            {img.desc  && <div className="text-white/60 text-xs font-mono mt-0.5">{img.desc}</div>}
          </div>
        )}
        {hasMultiple && (
          <div className="text-white/40 text-xs font-mono">{index + 1} / {images.length}</div>
        )}
      </div>

      {/* Next - fixed to viewport right-center */}
      {hasMultiple && (
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          style={{ position: "fixed", right: 12, top: "50%", transform: "translateY(-50%)", zIndex: 110 }}
          className="w-11 h-11 rounded-full bg-black/70 border border-white/20 text-white flex items-center justify-center hover:bg-white/20 transition-colors shadow-xl"
          aria-label="Next"
          data-testid="lightbox-next"
        >
          <ChevronRight size={24} />
        </button>
      )}
    </div>
  );
}

export function useLightbox() {
  const [state, setState] = React.useState({ images: [], index: -1 });
  const open  = (images, index = 0) => setState({ images, index });
  const close = () => setState({ images: [], index: -1 });
  const prev  = () => setState((s) => ({ ...s, index: (s.index - 1 + s.images.length) % s.images.length }));
  const next  = () => setState((s) => ({ ...s, index: (s.index + 1) % s.images.length }));
  const node  = state.index >= 0 ? <Lightbox images={state.images} index={state.index} onClose={close} onPrev={prev} onNext={next} /> : null;
  return { open, node };
}
